/**
 * Where each published JSON document lives, relative to the data prefix
 * (`data/` in the bucket, `/data` on the site). The one definition shared by
 * the writers (`store.ts`) and the readers (the web app, the history script),
 * so renaming a path can't leave one side behind.
 *
 * Pure and browser-safe, so it's on the root export rather than the
 * Node-only `/store` subpath.
 */
export const DATA_PATHS = {
  manifest: "manifest.json",
  payouts: "payouts.json",
  season: (season: number) => `seasons/${season}.json`,
  /** The raw hand-entered input behind a season (see `ManualSeasonInput`). */
  manualInput: (season: number) => `manual/${season}.json`,
  dues: (season: number) => `dues/${season}.json`,
  managerAliases: (season: number) => `manager-aliases/${season}.json`,
  payoutWinners: (season: number) => `payout-winners/${season}.json`,
  matchupIndex: "matchups/index.json",
  matchupReport: (season: number, week: number) => `matchups/${season}/week-${week}.json`,
  leagueHistory: "history/league.json",
  /**
   * The static page link-preview bots get for a site path like "/managers/greg"
   * (see `buildSharePages`). Throws for anything that isn't a plain lowercase path,
   * since the result becomes a bucket key.
   */
  sharePage: (sitePath: string) => {
    if (!/^\/[a-z0-9-]+(\/[a-z0-9-]+)*$/.test(sitePath)) throw new Error(`Not a shareable site path: ${sitePath}`);
    return `share${sitePath}.html`;
  },
  seasonHistory: (season: number) => `history/seasons/${season}.json`,
  /** Under `private/`, which the bucket policy hides from CloudFront (see infra/s3.tf). */
  matchupDraft: "private/matchup-draft.json",
} as const;
